class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

/**
 * HeaderMenu — the desktop navigation dropdown / mega menu custom element.
 *
 * Behavior layered on top of <details>:
 *   • Hover-intent open:  200ms debounce so a mouse sweeping across the
 *     nav bar doesn't pop every menu it passes over.
 *   • Hover-intent close: 300ms grace period after the cursor leaves
 *     both the trigger and the panel, so users can travel diagonally
 *     into the panel without losing it.
 *   • Keyboard focus also opens (opens on focusin of the summary).
 *   • Click still toggles natively via <details> (unchanged).
 *   • Escape closes the active menu and returns focus to its summary.
 *   • Single-open: opening one menu closes any others.
 *   • aria-expanded is kept in sync on open AND close.
 *
 * The sticky-header offset side effect from Dawn is preserved.
 */

const HOVER_OPEN_DELAY_MS = 200;
const HOVER_CLOSE_DELAY_MS = 300;

// Track the single currently-open menu at module scope.
let currentOpenHeaderMenu = null;

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');

    this._openTimer = null;
    this._closeTimer = null;

    // Pointer-based hover-intent. Guarded by pointer type so touch devices
    // don't accidentally trigger hover open (they'll get native click).
    this.addEventListener('pointerenter', this._onPointerEnter.bind(this));
    this.addEventListener('pointerleave', this._onPointerLeave.bind(this));

    // Keyboard focus opens the menu so keyboard-only users don't need to
    // press Enter on the summary.
    this.summary.addEventListener('focusin', this._onSummaryFocus.bind(this));

    // Ensure aria-expanded is correct on first paint.
    this.summary.setAttribute('aria-expanded', this.mainDetailsToggle.hasAttribute('open') ? 'true' : 'false');
  }

  _onPointerEnter(event) {
    // Strict allowlist: only true mouse/pen pointers trigger hover-intent.
    // '', 'touch', and any unknown type fall through to native <details>
    // click behavior on tap, which is the correct UX on touch devices.
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    this._cancelClose();
    this._openTimer = setTimeout(() => {
      this.openMenu();
    }, HOVER_OPEN_DELAY_MS);
  }

  _onPointerLeave(event) {
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    this._cancelOpen();
    this._closeTimer = setTimeout(() => {
      this.close();
    }, HOVER_CLOSE_DELAY_MS);
  }

  _onSummaryFocus() {
    // Only open on focus when focus arrived via keyboard (not a mouse
    // click — click already triggers <details> natively). We detect this
    // by checking :focus-visible on the summary.
    if (this.summary.matches(':focus-visible')) {
      this.openMenu();
    }
  }

  _cancelOpen() {
    if (this._openTimer) {
      clearTimeout(this._openTimer);
      this._openTimer = null;
    }
  }

  _cancelClose() {
    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
  }

  openMenu() {
    if (this.mainDetailsToggle.hasAttribute('open')) return;

    // Single-open invariant: close any other menu first.
    if (currentOpenHeaderMenu && currentOpenHeaderMenu !== this) {
      currentOpenHeaderMenu.close();
    }

    this.mainDetailsToggle.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', 'true');
    currentOpenHeaderMenu = this;
  }

  close() {
    this._cancelOpen();
    this._cancelClose();
    this.mainDetailsToggle.removeAttribute('open');
    this.summary.setAttribute('aria-expanded', 'false');
    if (currentOpenHeaderMenu === this) currentOpenHeaderMenu = null;
  }

  onToggle() {
    // Native toggle (e.g. summary click) — keep aria-expanded in sync and
    // respect the single-open invariant.
    const isOpen = this.mainDetailsToggle.hasAttribute('open');
    this.summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (isOpen) {
      if (currentOpenHeaderMenu && currentOpenHeaderMenu !== this) {
        currentOpenHeaderMenu.close();
      }
      currentOpenHeaderMenu = this;
    } else if (currentOpenHeaderMenu === this) {
      currentOpenHeaderMenu = null;
    }

    // Preserved from Dawn: expose header bottom position for sticky CSS.
    if (!this.header) return;
    this.header.preventHide = isOpen;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

// ── Global keydown: Escape closes the active menu and returns focus to
// its trigger. Registered once, not per instance.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' && event.key !== 'Esc') return;
  if (!currentOpenHeaderMenu) return;
  const menu = currentOpenHeaderMenu;
  menu.close();
  // Return focus to the summary the user opened, per accessibility spec.
  const summary = menu.querySelector('summary');
  if (summary) summary.focus();
});
